<template>
    <tr :data-review-line-id="line.id" :class="[{'review-source-row': Number(line.id) === reviewFocusedLineId, 'employee-focused-row': api.isEmployeeFocused(line)}, api.editStateClass(`detail:${line.id}`)]">
        <td class="timesheet-month-table__number">{{ lineIndex + 1 }}</td>
        <td
            v-if="ktuEnabled && !isMobileFullMonthView"
            class="timesheet-month-table__ktu"
            :class="[
                ktuClass(lineDetailDrafts[line.id].ktu),
                presenceCellClass(detailPresenceKey(line.id, 'ktu')),
                {'is-incomplete': assessmentIssues
                    .some(issue => issue.type.indexOf('ktu') !== -1)}
            ]"
            :style="presenceCellStyle(detailPresenceKey(line.id, 'ktu'))"
            :data-presence-key="detailPresenceKey(line.id, 'ktu')"
        >
            <select
                v-if="canEdit"
                v-model="lineDetailDrafts[line.id].ktu"
                :title="api.editStateTitle(`detail:${line.id}`)"
                :disabled="!canEdit"
                :aria-label="`КТУ: ${line.employee_name_snapshot}`"
                @focus="handleDetailFocus(line.id, 'ktu', $event)"
                @blur="handleDetailBlur(line.id, 'ktu')"
                @change="handleKtuChange(line)"
            >
                <option
                    v-for="option in ktuOptions"
                    :key="`ktu-${line.id}-${option.value}`"
                    :value="option.value"
                >
                    {{ option.text }}
                </option>
            </select>
            <strong v-else>{{ formatKtu(line.ktu) }}</strong>
            <span
                v-if="remoteEditorFor(detailPresenceKey(line.id, 'ktu'))"
                class="timesheet-presence-label"
            >
                {{ remoteEditorLabel(detailPresenceKey(line.id, 'ktu')) }}
            </span>
        </td>
        <th class="timesheet-month-table__person">
            <button type="button" class="timesheet-select-month" :aria-label="`Выделить весь месяц: ${line.employee_name_snapshot}`" title="Выделить весь месяц, включая свёрнутые недели" @click.stop="api.selectEmployeeMonth(line, $event)"><i class="far fa-calendar-alt" aria-hidden="true"></i></button>
            <div class="timesheet-person-name-row">
                <button
                    v-if="line.employee_id"
                    type="button"
                    class="timesheet-employee-link"
                    :title="assignmentPersonTitle(line)"
                    @click="openEmployeeInspector(line)"
                >
                    {{ lineEmployeeName(line) }}
                </button>
                <strong v-else :title="line.employee_name_snapshot">
                    {{ lineEmployeeName(line) }}
                </strong>
                <span
                    v-if="lineIssueGroups(line).length"
                    class="timesheet-issue-rail"
                    aria-label="Проблемы по сотруднику"
                >
                    <button
                        v-for="group in visibleLineIssueGroups(line)"
                        :key="`person-issue-${line.id}-${group.key}`"
                        type="button"
                        class="timesheet-issue-icon"
                        :class="issueCueClasses(group)"
                        :aria-label="issueGroupAriaLabel(group)"
                        v-b-tooltip.hover.focus.top="hasSourceIssue([group]) ? '' : issueGroupTooltip(group)"
                    @mouseenter="showSourcePopover($event, line, [group])"
                    @focus="showSourcePopover($event, line, [group])"
                    @keydown.down.prevent="showSourcePopover($event, line, [group], true)"
                    @keydown.esc.stop="closeSourcePopover()"
                        @click.stop="openIssueGroup(line, group)"
                    >
                        <i :class="group.icon" aria-hidden="true"></i>
                        <small v-if="group.count > 1">{{ group.count }}</small>
                    </button>
                    <button
                        v-if="lineIssueGroups(line).length > 3"
                        type="button"
                        class="timesheet-issue-icon timesheet-issue-icon--more"
                        :aria-label="`Ещё ${lineIssueGroups(line).length - 3} вида проблем`"
                        v-b-tooltip.hover.focus.top="lineIssueOverflowTooltip(line)"
                        @click.stop="openIssueGroup(line, lineIssueGroups(line)[3])"
                    >
                        +{{ lineIssueGroups(line).length - 3 }}
                    </button>
                </span>
            </div>
            <timesheet-row-actions v-if="api.canExcludeRosterLine(line)" :name="line.employee_name_snapshot || lineEmployeeName(line)" :disabled="api.rosterExclusionPending || api.editPendingCount > 0" @exclude="api.openRosterExclusion(line)" />
            <button
                v-if="line.unresolved_person_id"
                type="button"
                class="timesheet-unresolved-line-action"
                v-timesheet-attention
                :disabled="!canResolveUnresolved"
                @click.stop="openUnresolvedResolver(line)"
            >
                <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
                Нет карточки — сопоставить
            </button>
            <span v-else-if="line.can_remove_manual">Добавлен вручную</span>
            <button
                v-if="canRemoveManualLine(line)"
                type="button"
                class="timesheet-manual-line-remove"
                :disabled="removingLineId !== null"
                @click.stop="removeManualLine(line)"
            >
                <b-spinner v-if="removingLineId === Number(line.id)" small></b-spinner>
                <i v-else class="fas fa-trash-alt" aria-hidden="true"></i>
                Удалить из табеля
            </button>
            <b-button
                v-if="assignableLineIncident(line)"
                class="timesheet-line-assignment-action"
                size="sm"
                variant="outline-warning"
                :disabled="assigningIncidentId !== null"
                @click="assignEmployeeFromIncident(
                    line,
                    assignableLineIncident(line)
                )"
            >
                <i class="fas fa-user-plus" aria-hidden="true"></i>
                {{ assignmentActionLabel(assignableLineIncident(line)) }}
            </b-button>
        </th>
        <td
            v-if="ktuEnabled && isMobileFullMonthView"
            class="timesheet-month-table__ktu timesheet-month-table__ktu--mobile-scroll"
            :class="[
                ktuClass(lineDetailDrafts[line.id].ktu),
                presenceCellClass(detailPresenceKey(line.id, 'ktu')),
                {'is-incomplete': assessmentIssues
                    .some(issue => issue.type.indexOf('ktu') !== -1)}
            ]"
            :style="presenceCellStyle(detailPresenceKey(line.id, 'ktu'))"
            :data-presence-key="detailPresenceKey(line.id, 'ktu')"
        >
            <select
                v-if="canEdit"
                v-model="lineDetailDrafts[line.id].ktu"
                :title="api.editStateTitle(`detail:${line.id}`)"
                :disabled="!canEdit"
                :aria-label="`КТУ: ${line.employee_name_snapshot}`"
                @focus="handleDetailFocus(line.id, 'ktu', $event)"
                @blur="handleDetailBlur(line.id, 'ktu')"
                @change="handleKtuChange(line)"
            >
                <option
                    v-for="option in ktuOptions"
                    :key="`mobile-ktu-${line.id}-${option.value}`"
                    :value="option.value"
                >
                    {{ option.text }}
                </option>
            </select>
            <strong v-else>{{ formatKtu(line.ktu) }}</strong>
            <span
                v-if="remoteEditorFor(detailPresenceKey(line.id, 'ktu'))"
                class="timesheet-presence-label"
            >
                {{ remoteEditorLabel(detailPresenceKey(line.id, 'ktu')) }}
            </span>
        </td>
        <td
            v-for="column in visibleMonthColumns"
            :key="`${line.id}-${column.key}`"
            :data-line-id="line.id"
            :data-work-date="column.type === 'day' ? column.date : null"
            :title="columnPresentation[column.key].title"
            :data-presence-key="column.type === 'day' ? entryPresenceKey(line.id, column.date) : null"
            :class="[
                columnPresentation[column.key].classes,
                column.type === 'day'
                    ? presenceCellClass(entryPresenceKey(line.id, column.date))
                    : {}
            ]"
            :style="column.type === 'day'
                ? presenceCellStyle(entryPresenceKey(line.id, column.date))
                : {}"
        >
            <template v-if="column.type === 'day'">
                <timesheet-entry-input v-if="canEdit" :api="gridApi" :line="line" :date="column.date" class="timesheet-month-cell-input timesheet-keyboard-cell" :placeholder="monthCellPlaceholder(line, column.date)" :title="columnPresentation[column.key].title" />
                <timesheet-read-cell v-else :api="gridApi" :line="line" :date="column.date" :title="columnPresentation[column.key].title" />
                <span
                    v-if="hasExternalEntry(line, column.date) && localWorkedMinutes(line, column.date) > 0"
                    class="timesheet-month-external-badge"
                    title="Есть часы в другом табеле"
                >
                    Дт
                </span>
                <button
                    v-for="group in issueGroupsForDate(line, column.date).slice(0, 1)"
                    :key="`cell-issue-${line.id}-${column.date}-${group.key}`"
                    type="button"
                    class="timesheet-month-cell-flag"
                    :class="issueCueClasses(group)"
                    :aria-label="issueGroupsAriaLabel(issueGroupsForDate(line, column.date))"
                    v-b-tooltip.hover.focus.top="hasSourceIssue(issueGroupsForDate(line, column.date)) ? '' : issueGroupsTooltip(issueGroupsForDate(line, column.date))"
                    @mouseenter="showSourcePopover($event, line, issueGroupsForDate(line, column.date))"
                    @focus="showSourcePopover($event, line, issueGroupsForDate(line, column.date))"
                    @keydown.down.prevent="showSourcePopover($event, line, issueGroupsForDate(line, column.date), true)"
                    @keydown.esc.stop="closeSourcePopover()"
                    @click.stop="openCellIssue(line, column.date, column.week_key)"
                >
                    <i :class="group.icon" aria-hidden="true"></i>
                    <small v-if="issueGroupsForDate(line, column.date).length > 1">
                        +{{ issueGroupsForDate(line, column.date).length - 1 }}
                    </small>
                </button>
                <button
                    v-if="absenceDayFor(line, column.date) && absenceDayFor(line, column.date).state === 'suggestion'"
                    type="button"
                    class="timesheet-month-absence-suggestion"
                    :title="`Кадры предлагают ${absenceDayFor(line, column.date).code}. Нажмите, чтобы проставить.`"
                    :disabled="absenceApplying"
                    @click.stop="applyAbsenceSuggestions(line, column.date)"
                >
                    {{ absenceDayFor(line, column.date).code }}
                </button>
                <span
                    v-if="remoteEditorFor(entryPresenceKey(line.id, column.date))"
                    class="timesheet-presence-label"
                >
                    {{ remoteEditorLabel(entryPresenceKey(line.id, column.date)) }}
                </span>
            </template>
            <div v-else class="timesheet-week-summary">
                <button
                    type="button"
                    class="timesheet-week-summary-button"
                    :title="columnPresentation[column.key].title"
                    @click="toggleWeek(column.week_key)"
                >
                    <strong>{{ minutesLabel(weekLineMinutes(line, column.week)) }}</strong>
                    <span>ч</span>
                </button>
                <span
                    v-if="issueGroupsForWeek(line, column.week).length"
                    class="timesheet-week-summary__issues"
                    aria-label="Проблемы в свёрнутой неделе"
                >
                    <button
                        v-for="group in issueGroupsForWeek(line, column.week).slice(0, 2)"
                        :key="`week-issue-${line.id}-${column.week_key}-${group.key}`"
                        type="button"
                        class="timesheet-issue-icon timesheet-issue-icon--compact"
                        :class="issueCueClasses(group)"
                        :aria-label="issueGroupAriaLabel(group)"
                        v-b-tooltip.hover.focus.top="hasSourceIssue([group]) ? '' : issueGroupTooltip(group)"
                    @mouseenter="showSourcePopover($event, line, [group])"
                    @focus="showSourcePopover($event, line, [group])"
                    @keydown.down.prevent="showSourcePopover($event, line, [group], true)"
                    @keydown.esc.stop="closeSourcePopover()"
                        @click.stop="openIssueGroup(line, group, column.week_key)"
                    >
                        <i :class="group.icon" aria-hidden="true"></i>
                    </button>
                    <small v-if="issueGroupsForWeek(line, column.week).length > 2">
                        +{{ issueGroupsForWeek(line, column.week).length - 2 }}
                    </small>
                </span>
                <span
                    v-if="weekAssignmentCount(line, column.week)"
                    class="timesheet-week-summary__assignment"
                    :title="`${weekAssignmentCount(line, column.week)} назначенных дней`"
                >
                    <i class="fas fa-link" aria-hidden="true"></i>
                    {{ weekAssignmentCount(line, column.week) }}/{{ column.week.days.length }}
                </span>
            </div>
        </td>
        <template v-if="showMonthlyTotals">
            <td class="timesheet-month-table__total">
                <strong>{{ lineWorkDays(line) }}</strong>
            </td>
            <td class="timesheet-month-table__total">
                <strong>{{ minutesLabel(lineMonthMinutes(line)) }}</strong>
            </td>
            <td class="timesheet-month-table__total">
                <strong>{{ lineAverageHours(line) }}</strong>
            </td>
        </template>
        <td v-else class="timesheet-month-table__total is-collapsed-total">
            <button
                type="button"
                :title="`Развернуть итоги: ${lineWorkDays(line)} дн., ${minutesLabel(lineMonthMinutes(line))} ч., среднее ${lineAverageHours(line)} ч.`"
                @click="showMonthlyTotals = true"
            >
                <strong>{{ minutesLabel(lineMonthMinutes(line)) }}</strong>
                <span>ч</span>
            </button>
        </td>
        <td class="timesheet-month-table__assessment">
            <button
                type="button"
                class="timesheet-assessment-button"
                :class="assessmentIssues.length
                    ? 'is-incomplete'
                    : 'is-complete'"
                :aria-label="`Характеристика: ${line.employee_name_snapshot}`"
                @click="openAssessment(line)"
            >
                <i class="fas fa-user-check" aria-hidden="true"></i>
                <span>{{ assessmentIssues.length ? '!' : '✓' }}</span>
            </button>
        </td>
        <td
            v-for="field in visibleCharacteristicFields"
            :key="`${line.id}-characteristic-${field.key}`"
            class="timesheet-month-table__characteristic"
            :title="api.characteristicAuthorLabel(line, field)"
            :data-presence-key="detailPresenceKey(line.id, `characteristic:${field.key}`)"
            :class="[
                {'is-text': field.type === 'text'},
                presenceCellClass(detailPresenceKey(line.id, `characteristic:${field.key}`))
            ]"
            :style="presenceCellStyle(
                detailPresenceKey(line.id, `characteristic:${field.key}`)
            )"
        >
            <button v-if="canEdit" type="button" class="btn btn-link p-0 text-left timesheet-characteristic-open" @click="openAssessment(line)">
                {{ characteristicDisplay(line, field) || 'Заполнить характеристику' }}
            </button>
            <span v-else>{{ characteristicDisplay(line, field) }}</span>
            <span
                v-if="remoteEditorFor(
                    detailPresenceKey(line.id, `characteristic:${field.key}`)
                )"
                class="timesheet-presence-label"
            >
                {{ remoteEditorLabel(
                    detailPresenceKey(line.id, `characteristic:${field.key}`)
                ) }}
            </span>
        </td>
    </tr>
</template>
<script>
import TimesheetRowActions from './TimesheetRowActions.vue';
import TimesheetEntryInput from './TimesheetEntryInput.vue';
import TimesheetReadCell from './TimesheetReadCell.vue';
import timesheetAttention from './timesheetAttention';
export default {
    directives: {timesheetAttention},
    name: 'TimesheetMonthRow',
    components: {TimesheetRowActions, TimesheetEntryInput, TimesheetReadCell},
    props: {api: {type: Object, required: true}, line: {type: Object, required: true}, lineIndex: {type: Number, required: true}},
    computed: {
        columnPresentation() {
            const result = {};
            this.visibleMonthColumns.forEach(column => { result[column.key] = {title: this.api.monthColumnTitle(this.line, column), classes: this.api.monthColumnClasses(this.line, column)}; });
            return result;
        },
        assessmentIssues() { return this.api.assessmentIssuesForLine(this.line, false); },
        gridApi() { return this.api; },
        lineDetailDrafts() { return this.api.lineDetailDrafts; },
        assigningIncidentId() { return this.api.assigningIncidentId; },
        showMonthlyTotals() { return this.api.showMonthlyTotals; },
        absenceApplying() { return this.api.absenceApplying; },
        removingLineId() { return this.api.removingLineId; },
        visibleCharacteristicFields() { return this.api.visibleCharacteristicFields; },
        ktuEnabled() { return this.api.ktuEnabled; },
        ktuOptions() { return this.api.ktuOptions; },
        canEdit() { return this.api.canEdit; },
        canResolveUnresolved() { return this.api.canResolveUnresolved; },
        isMobileFullMonthView() { return this.api.isMobileFullMonthView; },
        visibleMonthColumns() { return this.api.visibleMonthColumns; },
        reviewFocusedLineId() { return this.api.reviewFocusedLineId; }
    },
    methods: {
        hasSourceIssue(...args) { return this.api.hasSourceIssue(...args); },
        showSourcePopover(...args) { return this.api.showSourcePopover(...args); },
        closeSourcePopover(...args) { return this.api.closeSourcePopover(...args); },
        entryPresenceKey(...args) { return this.api.entryPresenceKey(...args); },
        detailPresenceKey(...args) { return this.api.detailPresenceKey(...args); },
        handleDetailFocus(...args) { return this.api.handleDetailFocus(...args); },
        handleDetailBlur(...args) { return this.api.handleDetailBlur(...args); },
        remoteEditorFor(...args) { return this.api.remoteEditorFor(...args); },
        remoteEditorLabel(...args) { return this.api.remoteEditorLabel(...args); },
        presenceCellClass(...args) { return this.api.presenceCellClass(...args); },
        presenceCellStyle(...args) { return this.api.presenceCellStyle(...args); },
        lineEmployeeName(...args) { return this.api.lineEmployeeName(...args); },
        openEmployeeInspector(...args) { return this.api.openEmployeeInspector(...args); },
        toggleWeek(...args) { return this.api.toggleWeek(...args); },
        assignmentPersonTitle(...args) { return this.api.assignmentPersonTitle(...args); },
        weekAssignmentCount(...args) { return this.api.weekAssignmentCount(...args); },
        absenceDayFor(...args) { return this.api.absenceDayFor(...args); },
        monthCellPlaceholder(...args) { return this.api.monthCellPlaceholder(...args); },
        openAssessment(...args) { return this.api.openAssessment(...args); },
        handleKtuChange(...args) { return this.api.handleKtuChange(...args); },
        applyAbsenceSuggestions(...args) { return this.api.applyAbsenceSuggestions(...args); },
        openDay(...args) { return this.api.openDay(...args); },
        localWorkedMinutes(...args) { return this.api.localWorkedMinutes(...args); },
        lineIssueGroups(...args) { return this.api.lineIssueGroups(...args); },
        visibleLineIssueGroups(...args) { return this.api.visibleLineIssueGroups(...args); },
        issueGroupsForDate(...args) { return this.api.issueGroupsForDate(...args); },
        issueGroupsForWeek(...args) { return this.api.issueGroupsForWeek(...args); },
        issueGroupTooltip(...args) { return this.api.issueGroupTooltip(...args); },
        issueGroupsTooltip(...args) { return this.api.issueGroupsTooltip(...args); },
        lineIssueOverflowTooltip(...args) { return this.api.lineIssueOverflowTooltip(...args); },
        issueGroupAriaLabel(...args) { return this.api.issueGroupAriaLabel(...args); },
        issueGroupsAriaLabel(...args) { return this.api.issueGroupsAriaLabel(...args); },
        issueCueClasses(...args) { return this.api.issueCueClasses(...args); },
        openIssueGroup(...args) { return this.api.openIssueGroup(...args); },
        openCellIssue(...args) { return this.api.openCellIssue(...args); },
        assignableLineIncident(...args) { return this.api.assignableLineIncident(...args); },
        assignmentActionLabel(...args) { return this.api.assignmentActionLabel(...args); },
        assignEmployeeFromIncident(...args) { return this.api.assignEmployeeFromIncident(...args); },
        hasExternalEntry(...args) { return this.api.hasExternalEntry(...args); },
        monthCellDisplay(...args) { return this.api.monthCellDisplay(...args); },
        weekLineMinutes(...args) { return this.api.weekLineMinutes(...args); },
        lineMonthMinutes(...args) { return this.api.lineMonthMinutes(...args); },
        lineWorkDays(...args) { return this.api.lineWorkDays(...args); },
        lineAverageHours(...args) { return this.api.lineAverageHours(...args); },
        characteristicDisplay(...args) { return this.api.characteristicDisplay(...args); },
        formatKtu(...args) { return this.api.formatKtu(...args); },
        ktuClass(...args) { return this.api.ktuClass(...args); },
        minutesLabel(...args) { return this.api.minutesLabel(...args); },
        openUnresolvedResolver(...args) { return this.api.openUnresolvedResolver(...args); },
        canRemoveManualLine(...args) { return this.api.canRemoveManualLine(...args); },
        removeManualLine(...args) { return this.api.removeManualLine(...args); }
    }
};
</script>
<style scoped>
.timesheet-characteristic-open {
    display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 3;
    overflow: hidden; width: 100%; white-space: normal; overflow-wrap: anywhere; font: inherit;
}
</style>
